import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Skeleton,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import {
  CompanyDocumentType,
  CompanyDraftStatus,
  DocumentStatus,
  IdentityType,
  SpvDraftDocumentInfosFragment,
  useGetSpvDraftDocumentsQuery,
  useGetSpvDraftQuery,
} from '@blockpulse3/graphql/hooks';
import { ErrorQueryCard, SignatureDocument } from '@blockpulse3/ui/commons';
import { useBadge } from '@blockpulse3/ui/ui-hooks';

import { SPV_DOCUMENT_TYPE_TO_SIGN, cleanDraftDocuments } from '../../../utils';

export type SelectableCompanyDocumentType = Extract<
  CompanyDocumentType,
  | CompanyDocumentType.DBE
  | CompanyDocumentType.DNC
  | CompanyDocumentType.DOMICILIATION
  | CompanyDocumentType.POUVOIR_FORMALITES
  | CompanyDocumentType.SOUSCRIPTEURS
  | CompanyDocumentType.STATUTS
  | CompanyDocumentType.FORMULAIRE_M0
>;

type Props = {
  onDocumentClick: (
    type: SelectableCompanyDocumentType,
    doc: SpvDraftDocumentInfosFragment,
  ) => void;
};

let spvDraftTimeoutId: NodeJS.Timeout | null = null;

/**
 * SPVMatriculationDocuments.
 * Document section with signature status.
 *
 * @returns {JSX.Element}
 */
export function SPVMatriculationDocuments({ onDocumentClick }: Props): JSX.Element {
  const i18nSPVMatriculationDocuments = useTranslations('SPVMatriculationDocuments');
  const [isPollingStarted, setIsPollingStarted] = useState<boolean>(false);
  const [isPollingLoading, setIsPollingLoading] = useState<boolean>(false);
  const [isAccordionOpen, setIsAccordionOpen] = useState<boolean>(false);

  const { id = '' } = useParams();

  const { data: spvData, loading, error, refetch } = useGetSpvDraftQuery({ variables: { id } });
  const spvDraftDocumentsReq = useGetSpvDraftDocumentsQuery({
    variables: { id },
  });
  const spvDraftDocuments = spvDraftDocumentsReq.data?.spvDraftDocuments;

  const { getBadge } = useBadge(
    null,
    [{ value: true, label: i18nSPVMatriculationDocuments('Badge.Complete'), color: 'green' }],
    { label: i18nSPVMatriculationDocuments('Badge.RequiredAction'), color: 'gray' },
  );

  const isIndividual =
    spvData?.spvDraft.content.representatives[0].type === IdentityType.INDIVIDUAL;

  const allDocumentsSigned =
    spvDraftDocuments &&
    SPV_DOCUMENT_TYPE_TO_SIGN.every(
      (docType) =>
        spvDraftDocuments?.some((doc) => {
          if (!isIndividual && docType === CompanyDocumentType.DNC) return true;
          return doc.type === docType && doc.document.status === DocumentStatus.SIGNED;
        }),
    );

  useEffect(() => {
    const activeStep =
      spvData?.spvDraft.status &&
      [CompanyDraftStatus.PAYED, CompanyDraftStatus.VALIDATED].includes(spvData.spvDraft.status) &&
      !allDocumentsSigned
        ? true
        : false;

    setIsAccordionOpen(activeStep);
  }, [allDocumentsSigned, spvData, spvDraftDocuments]);

  useEffect(() => {
    if (!spvDraftDocuments?.length) return;

    const isAnyDocumentPending = Object.values(cleanDraftDocuments(spvDraftDocuments)).some((doc) =>
      [DocumentStatus.SIGNATURES_PENDING, DocumentStatus.ONGOING].includes(doc.document.status),
    );

    if (!isPollingStarted && isAnyDocumentPending) {
      setIsPollingStarted(true);
      setIsPollingLoading(true);
      spvDraftDocumentsReq.startPolling(2000);

      if (spvDraftTimeoutId) clearTimeout(spvDraftTimeoutId);
      spvDraftTimeoutId = setTimeout(() => {
        spvDraftDocumentsReq.stopPolling();
        setIsPollingLoading(false);
      }, 20_000);
    }

    if (isPollingStarted && !isAnyDocumentPending) {
      spvDraftDocumentsReq.stopPolling();
      setIsPollingLoading(false);
      refetch();
    }
  }, [isPollingStarted, spvDraftDocuments, spvDraftDocumentsReq, refetch]);

  useEffect(() => {
    return () => {
      if (spvDraftTimeoutId) {
        clearTimeout(spvDraftTimeoutId);
      }
    };
  }, []);

  if (loading) {
    return <Skeleton h="65px" />;
  }

  if (error) {
    return <ErrorQueryCard h="65px" />;
  }

  if (!spvData) {
    return <ErrorQueryCard h="65px" />;
  }

  const draft = spvData.spvDraft;

  /* ** Track draft status; disabled accordion when draft status is editing ** */
  const isEditing = draft.status === CompanyDraftStatus.EDITING;

  const spvDocuments: Record<string, SpvDraftDocumentInfosFragment> = (spvDraftDocuments || [])
    .filter((doc) => SPV_DOCUMENT_TYPE_TO_SIGN.includes(doc.type))
    .reduce((acc, cur) => ({ ...acc, [cur.type]: cur }), {});

  const badge = getBadge(allDocumentsSigned);

  const handleDocumentClick = (
    type: SelectableCompanyDocumentType,
    doc: SpvDraftDocumentInfosFragment,
  ): void => {
    onDocumentClick(type, doc);
  };

  const handleAccordionOpen = (): void => {
    setIsAccordionOpen(!isAccordionOpen);
  };

  return (
    <Accordion
      key={`documents${draft?.id}`}
      allowMultiple
      index={isAccordionOpen ? [0] : [-1]}
      onChange={handleAccordionOpen}
    >
      <AccordionItem isDisabled={isEditing}>
        <AccordionButton>
          <AccordionIcon boxSize="8" />
          <Box flex="1" p="2" textAlign="left">
            <Text fontSize="xl" fontWeight="semibold">
              {i18nSPVMatriculationDocuments('Title')}
            </Text>
          </Box>
          <Badge colorScheme={badge.color}>{badge.label}</Badge>
        </AccordionButton>
        <AccordionPanel>
          <Stack spacing="4">
            <Alert status="info">
              <AlertIcon />
              <AlertTitle>
                {i18nSPVMatriculationDocuments.rich('Description', {
                  bold: (chunks) => <b>{chunks}</b>,
                  underline: (chunks) => <u>{chunks}</u>,
                })}
              </AlertTitle>
            </Alert>
            <Stack spacing="2">
              <SignatureDocument
                isBadgeLoading={isPollingLoading}
                status={spvDocuments[CompanyDocumentType.STATUTS]?.document?.status}
                title={i18nSPVMatriculationDocuments('DocumentStatus')}
                onClick={(): void =>
                  handleDocumentClick(
                    CompanyDocumentType.STATUTS,
                    spvDocuments[CompanyDocumentType.STATUTS],
                  )
                }
              />
              <SignatureDocument
                isBadgeLoading={isPollingLoading}
                status={spvDocuments[CompanyDocumentType.DOMICILIATION]?.document?.status}
                title={i18nSPVMatriculationDocuments('DocumentAddress')}
                onClick={(): void =>
                  handleDocumentClick(
                    CompanyDocumentType.DOMICILIATION,
                    spvDocuments[CompanyDocumentType.DOMICILIATION],
                  )
                }
              />
              {isIndividual && (
                <SignatureDocument
                  isBadgeLoading={isPollingLoading}
                  status={spvDocuments[CompanyDocumentType.DNC]?.document?.status}
                  title={i18nSPVMatriculationDocuments('DocumentDNC')}
                  onClick={(): void =>
                    handleDocumentClick(
                      CompanyDocumentType.DNC,
                      spvDocuments[CompanyDocumentType.DNC],
                    )
                  }
                />
              )}
              <SignatureDocument
                isBadgeLoading={isPollingLoading}
                status={spvDocuments[CompanyDocumentType.SOUSCRIPTEURS]?.document?.status}
                title={i18nSPVMatriculationDocuments('DocumentSubscriber')}
                onClick={(): void =>
                  handleDocumentClick(
                    CompanyDocumentType.SOUSCRIPTEURS,
                    spvDocuments[CompanyDocumentType.SOUSCRIPTEURS],
                  )
                }
              />
              <SignatureDocument
                isBadgeLoading={isPollingLoading}
                status={spvDocuments[CompanyDocumentType.DBE]?.document?.status}
                title={i18nSPVMatriculationDocuments('DocumentDBE')}
                onClick={(): void =>
                  handleDocumentClick(
                    CompanyDocumentType.DBE,
                    spvDocuments[CompanyDocumentType.DBE],
                  )
                }
              />
              <SignatureDocument
                isBadgeLoading={isPollingLoading}
                status={spvDocuments[CompanyDocumentType.POUVOIR_FORMALITES]?.document?.status}
                title={i18nSPVMatriculationDocuments('DocumentFormalites')}
                onClick={(): void =>
                  handleDocumentClick(
                    CompanyDocumentType.POUVOIR_FORMALITES,
                    spvDocuments[CompanyDocumentType.POUVOIR_FORMALITES],
                  )
                }
              />
              <SignatureDocument
                isBadgeLoading={isPollingLoading}
                status={spvDocuments[CompanyDocumentType.FORMULAIRE_M0]?.document?.status}
                title={i18nSPVMatriculationDocuments('DocumentFormM0')}
                onClick={(): void =>
                  handleDocumentClick(
                    CompanyDocumentType.FORMULAIRE_M0,
                    spvDocuments[CompanyDocumentType.FORMULAIRE_M0],
                  )
                }
              />
            </Stack>
          </Stack>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
}

export type SPVMatriculationDocumentsProps = Props;
